/** Shared dependencies and lifecycle for the theme's existing text animations. */
(function () {
  if (window.DreaverAnimationLoader) return;

  const registrations = new Map();
  let dependencyPromise;

  function loadScript(src, globalName) {
    if (window[globalName]) return Promise.resolve(window[globalName]);

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      function fail() {
        script.onload = script.onerror = null;
        script.remove();
        reject(new Error('Could not load ' + globalName + ' for text animations.'));
      }

      script.onload = function () {
        if (!window[globalName]) {
          fail();
          return;
        }
        script.onload = script.onerror = null;
        resolve(window[globalName]);
      };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }

  function loadDependencies() {
    if (!dependencyPromise) {
      dependencyPromise = loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js', 'gsap')
        .then(gsap => loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js', 'ScrollTrigger')
          .then(ScrollTrigger => {
            gsap.registerPlugin(ScrollTrigger);
            return { gsap, ScrollTrigger };
          }))
        .catch(error => {
          // A later editor section load may retry; never retry in a loop.
          dependencyPromise = undefined;
          throw error;
        });
    }
    return dependencyPromise;
  }

  function restoreTargets(targets) {
    targets.forEach(({ element, children, style }) => {
      // Restore only animation-owned text targets, retaining their original nodes.
      // Buttons, accordions, media and slider DOM elsewhere in the section stay intact.
      if (element.childNodes.length !== children.length || children.some((node, index) => element.childNodes[index] !== node)) {
        element.replaceChildren(...children);
      }
      if (style === null) element.removeAttribute('style');
      else element.setAttribute('style', style);
    });
  }

  function dispose(registration, section, instance) {
    instance.cancelled = true;
    if (registration.instances.get(section) === instance) registration.instances.delete(section);
    try {
      instance.context?.revert();
    } catch (error) {
      console.warn('[Dreaver animations] Could not revert an animation context.', error);
    } finally {
      restoreTargets(instance.targets);
      instance.context = null;
      instance.targets = [];
    }
  }

  function initialize(registration, section) {
    if (!section.isConnected || registration.instances.has(section)) return;
    if (!section.querySelector(registration.targetSelector)) return;

    const instance = {
      owner: section.closest('.shopify-section'),
      cancelled: false,
      context: null,
      targets: []
    };
    registration.instances.set(section, instance);

    loadDependencies().then(dependencies => {
      // Unloading and reloading the same DOM node must invalidate its old promise.
      if (instance.cancelled || registration.instances.get(section) !== instance) return;
      if (!section.isConnected) {
        dispose(registration, section, instance);
        return;
      }

      instance.targets = Array.from(section.querySelectorAll(registration.targetSelector), element => ({
        element,
        children: Array.from(element.childNodes),
        style: element.getAttribute('style')
      }));
      // Keep the context before invoking the callback so partial failures can revert.
      instance.context = dependencies.gsap.context(() => {}, section);
      // GSAP restores its active context only after this callback returns.
      // Contain setup errors here so a later section cannot inherit this context.
      let setupFailed = false;
      let setupError;
      instance.context.add(() => {
        try {
          return registration.animate(section, dependencies);
        } catch (error) {
          setupFailed = true;
          setupError = error;
        }
      });
      if (setupFailed) throw setupError;
    }).catch(error => {
      if (instance.cancelled) return;
      dispose(registration, section, instance);
      console.warn('[Dreaver animations] Leaving section text unanimated.', error);
    });
  }

  function scan(root, registration) {
    registration.instances.forEach((instance, section) => {
      if (!section.isConnected) dispose(registration, section, instance);
    });
    if (root.matches?.(registration.sectionSelector)) initialize(registration, root);
    root.querySelectorAll(registration.sectionSelector).forEach(section => initialize(registration, section));
  }

  function scanAll(root) {
    registrations.forEach(registration => scan(root, registration));
  }

  window.DreaverAnimationLoader = {
    register({ key, sectionSelector, targetSelector, animate }) {
      if (!registrations.has(key)) {
        registrations.set(key, { sectionSelector, targetSelector, animate, instances: new Map() });
      }
      if (document.readyState !== 'loading') scan(document, registrations.get(key));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanAll(document), { once: true });
  }
  document.addEventListener('shopify:section:load', event => scanAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    registrations.forEach(registration => {
      registration.instances.forEach((instance, section) => {
        if (section === event.target || instance.owner === event.target || event.target.contains(section)) {
          dispose(registration, section, instance);
        }
      });
    });
  });
})();
